/* ═══════════════════════════════════════════════════════════════
   Spec Library — Spec Master catalog with draft indicators
   Shows published spec masters, draft status, and pending PRs
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { rem } from '../lib/rem';
import { toAddress } from '../routing/address';
import { DataTable, Column, GroupOption } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { DivisionRail } from '../components/DivisionRail';
import { ExportMastersDialog } from './ExportMastersDialog';
import EmptyState from '../components/EmptyState';
import {
  SpecService,
  diffBlocks,
  diffChangeSummary,
  ChangeSummary,
  csiConflict,
  cleanSaveError,
  nextDuplicateNumber,
} from '../services/specService';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  isWordFile,
  isLegacyWordFile,
  LEGACY_WORD_NOT_PROCESSED_MESSAGE,
} from '../../shared/wordFiles';
import { SpecMasterService } from '../services/specMasterService';
import { pushEditHistory } from '../../shared/editHistory';
import { hasPermission } from '../../shared/permissions';
import { csiDivisionGroupLabel } from '../../shared/zone-layout';
import { isWeb } from '../lib/platform';
import { activeOnly } from '../lib/archive';
import { StatusBadge } from '../components/StatusBadge';
import type { SpecMasterDraft, BlockDiff, SpecMaster } from '../../shared/types';

interface SpecLibraryProps {
  /** `openPublish`: land in the Studio with the publish dialog open (a
   *  review-queue Approve — the approver answers every variant there). */
  onOpenEditor: (id: string, opts?: { openPublish?: boolean }) => void;
  toast: (msg: string, isError?: boolean) => void;
  userRole?: 'site_admin' | 'admin' | 'manager' | 'member';
  userName?: string;
  /** Granular permission keys from AuthUser.permissions. Falls back to userRole if omitted. */
  userPermissions?: string[];
}

/** One imported file that did not make it, with a plain reason. */
interface ImportFailure {
  file: string;
  reason: string;
}

/** What the PM answered for one import conflict. */
type ConflictChoice = { action: 'skip' } | { action: 'import'; csi: string; title: string };

/** A confirmation waiting on an answer (replaces the old native confirm()). */
interface PendingConfirm {
  title: string;
  description: string;
  confirmLabel: string;
  destructive: boolean;
  run: () => Promise<void>;
}

/** Conflict resolution for imported spec that matches an existing master */
interface ImportConflict {
  /** The extracted data from the docx */
  csiNumber: string;
  title: string;
  blocks: any[];
  division: string;
  /** The existing master that conflicts */
  existingMaster: any;
}

// ─── Token-derived soft tints (no raw hex — see docs/DESIGN.md §2) ───
const WARN_TEXT = 'color-mix(in srgb, var(--color-warning) 60%, var(--tx))';
const WARN_SOFT = 'color-mix(in srgb, var(--color-warning) 12%, transparent)';
const WARN_SOFT_HOVER = 'color-mix(in srgb, var(--color-warning) 18%, transparent)';
const WARN_BORDER = 'color-mix(in srgb, var(--color-warning) 40%, transparent)';
const DANGER_SOFT = 'color-mix(in srgb, var(--dg) 8%, transparent)';

// ─── Inline Lucide-style icons (no emoji as icons — rule no-emoji-icons) ───
// Icons follow the text beside them (DESIGN.md §3.6): `em` sizes relative to
// the inherited font-size; `size` (px) stays as a fallback for any caller
// that hasn't migrated.
interface LocalIconProps {
  size?: number;
  em?: number;
}
function SvgIcon({ size = 14, em, children }: LocalIconProps & { children: React.ReactNode }) {
  const dim = em != null ? `${em}em` : size;
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}
const LockIcon = ({ size = 12, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </SvgIcon>
);
const UnlockIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
  </SvgIcon>
);
const CopyIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </SvgIcon>
);
const PublishIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </SvgIcon>
);
const CheckIcon = ({ size = 12, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <polyline points="20 6 9 17 4 12" />
  </SvgIcon>
);
const PlusIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </SvgIcon>
);
const AlertIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </SvgIcon>
);
const DownloadIcon = ({ size = 14, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </SvgIcon>
);
const BookIcon = ({ size = 28, em }: LocalIconProps) => (
  <SvgIcon size={size} em={em}>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
  </SvgIcon>
);

/** Skeleton shown during the first data load (skeletons over spinners — DESIGN.md §4). */
function LibrarySkeleton() {
  return (
    <div
      role="status"
      aria-label="Loading master specs"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
    >
      <div
        style={{
          height: rem(36),
          maxWidth: rem(320),
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          animation: 'pulse 1.4s ease-in-out infinite',
        }}
      />
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: rem(40),
            background: 'var(--sa)',
            borderRadius: 'var(--radius-md)',
            opacity: Math.max(0.25, 0.95 - i * 0.1),
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

function SpecLibrary({
  onOpenEditor,
  toast,
  userRole = 'admin',
  userName = 'user',
  userPermissions,
}: SpecLibraryProps) {
  // Permission-first. Every gate reads from userPermissions; no role
  // fallback. Custom firm roles compose their exact permission set in
  // the admin center, so a role-based fallback can't express them.
  const permCan = (key: string) => hasPermission(userPermissions, key);
  const [masters, setMasters] = React.useState<any[]>([]);
  const [drafts, setDrafts] = React.useState<SpecMasterDraft[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  // Search is handled by DataTable's built-in search bar
  const [sortBy, setSortBy] = React.useState('csi');
  // View mode (table/tile) is owned by DataTable now — defaults to table per Josh's rule.
  const [selectedDiv, setSelectedDiv] = React.useState<string | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);
  const [expandedDivs, setExpandedDivs] = React.useState<any>({});
  const [showNewModal, setShowNewModal] = React.useState(false);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [reviewDraft, setReviewDraft] = React.useState<SpecMasterDraft | null>(null);
  const [reviewDiff, setReviewDiff] = React.useState<BlockDiff[]>([]);
  const [reviewChangeSummary, setReviewChangeSummary] = React.useState<ChangeSummary | null>(null);
  const [reviewNote, setReviewNote] = React.useState('');
  const [newCsi, setNewCsi] = React.useState('');
  const [newTitle, setNewTitle] = React.useState('');

  // Import state
  const [importing, setImporting] = React.useState(false);
  const [importStatus, setImportStatus] = React.useState('');
  const [importConflict, setImportConflict] = React.useState<ImportConflict | null>(null);
  const [conflictNewCsi, setConflictNewCsi] = React.useState('');
  const [conflictNewTitle, setConflictNewTitle] = React.useState('');
  const [conflictApplyAll, setConflictApplyAll] = React.useState(false);
  const [importFailures, setImportFailures] = React.useState<ImportFailure[] | null>(null);
  // The import loop waits on this while a conflict dialog is open.
  const conflictResolverRef = React.useRef<((c: ConflictChoice, all: boolean) => void) | null>(
    null
  );
  // Masters seen by the running import, including ones it just created.
  const knownMastersRef = React.useRef<Array<{ id?: string; csiNumber?: string; title?: string }>>(
    []
  );
  // Row selection is controlled so a fresh duplicate can be selected after reload.
  const [libSelected, setLibSelected] = React.useState<Set<string>>(new Set());
  const [exportTargets, setExportTargets] = React.useState<SpecMaster[] | null>(null);
  const [pendingConfirm, setPendingConfirm] = React.useState<PendingConfirm | null>(null);
  const [confirmBusy, setConfirmBusy] = React.useState(false);

  const canCreate = permCan('specs.create');
  // "Can edit" at the library level means either owning a draft or being
  // able to take over anyone's draft. Library-level lets us show the Edit
  // button; the editor itself further gates individual edit actions.
  const canEdit = permCan('specs.edit_own') || permCan('specs.edit_any');
  const canReview = permCan('specs.approve_review');
  const canPublish = permCan('specs.publish');
  const canArchive = permCan('specs.archive');
  const canOverrideDraft = permCan('specs.override_draft');
  const canImport = permCan('specs.import');

  React.useEffect(() => {
    loadData();
  }, []);
  // Leaving the page with a conflict dialog open must not strand the import loop.
  React.useEffect(
    () => () => {
      conflictResolverRef.current?.({ action: 'skip' }, false);
    },
    []
  );
  React.useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadData() {
    // Promise.allSettled, never Promise.all — data never blank (DESIGN.md §5).
    const [listRes, draftRes] = await Promise.allSettled([
      SpecService.listMasters(),
      SpecService.listDrafts(),
    ]);
    // Copy: cloudCache mutates its row array in place, so the same reference
    // would make React skip the re-render after Duplicate / Import.
    if (listRes.status === 'fulfilled') setMasters([...listRes.value]);
    if (draftRes.status === 'fulfilled') setDrafts([...draftRes.value]);
    if (listRes.status === 'rejected' || draftRes.status === 'rejected') {
      setLoadError(
        "Couldn't load the spec library. Check your connection — the list refreshes automatically when sync completes."
      );
    } else {
      setLoadError(null);
    }
    setLoading(false);
  }

  // Pending PRs for admin review
  // A draft row exists only once someone actually changed the document (co-editing);
  // the legacy locked_by field can be stale, so the draft row drives status + lock.
  const draftByMaster = React.useMemo(() => {
    const map = new Map<string, SpecMasterDraft>();
    for (const d of drafts) {
      const prev = map.get(d.blueprintId);
      if (!prev || (d.lastSaved || '') > (prev.lastSaved || '')) map.set(d.blueprintId, d);
    }
    return map;
  }, [drafts]);
  const hasDraft = (m: { id: string }) => draftByMaster.has(m.id);
  /** Who holds the in-progress draft: the draft's editor, else the legacy lock (no draft row). */
  const draftHolder = (m: { id: string; lockedBy?: string | null }): string | null =>
    draftByMaster.get(m.id)?.editedBy ?? m.lockedBy ?? null;
  const isHeld = (m: { id: string; lockedBy?: string | null }) => hasDraft(m) || !!m.lockedBy;
  const heldByOther = (m: { id: string; lockedBy?: string | null }) =>
    isHeld(m) && draftHolder(m) !== userName;

  const pendingPRs = React.useMemo(
    () => drafts.filter((d) => d.publishRequest?.status === 'pending'),
    [drafts]
  );

  const divisions = SpecService.getDivisions();
  const filtered = React.useMemo(() => {
    let result = activeOnly(masters, showArchived);
    if (selectedDiv) result = result.filter((m: any) => m.division === selectedDiv);
    // Search is now handled by DataTable's built-in search — no duplicate filtering here
    result = [...result].sort((a: any, b: any) => {
      if (sortBy === 'csi') return a.csiNumber.localeCompare(b.csiNumber);
      if (sortBy === 'title') return a.title.localeCompare(b.title);
      if (sortBy === 'modified') return (b.lastModified || '').localeCompare(a.lastModified || '');
      return 0;
    });
    return result;
  }, [masters, selectedDiv, sortBy, showArchived]);

  // "Show archived" off ⇒ the division rail counts active specs only — a
  // division that is entirely archived should look empty until toggled on.
  const divCounts = React.useMemo(() => {
    const counts: any = {};
    activeOnly(masters, showArchived).forEach((m: any) => {
      counts[m.division] = (counts[m.division] || 0) + 1;
    });
    return counts;
  }, [masters, showArchived]);

  // ─── CSI uniqueness: one Master Spec per CSI number (case/whitespace-insensitive).
  // Variants live INSIDE a master; two masters can never share a number.
  // `csiConflict` (specService.ts) is the ONE shared check — rename, New,
  // and Save As all call it so the rule can't drift between surfaces. ───
  function csiExists(csi: string, ignoreId?: string): any | null {
    return csiConflict(masters, csi, ignoreId);
  }

  async function handleNew() {
    if (!newCsi.trim()) {
      toast('Enter a CSI number', true);
      return;
    }
    const clash = csiExists(newCsi);
    if (clash) {
      toast(`Section ${newCsi.trim()} is already used by "${clash.title}"`, true);
      return;
    }
    const master = {
      id: SpecService.generateId(),
      csiNumber: newCsi.trim(),
      title: newTitle.trim() || 'Untitled Specification',
      division: newCsi.trim().substring(0, 2),
      status: 'draft',
      version: 1,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
      coreContent: [
        { id: SpecService.generateId(), level: 1, text: 'GENERAL' },
        { id: SpecService.generateId(), level: 2, text: 'SUMMARY' },
        { id: SpecService.generateId(), level: 3, text: 'Section includes...' },
        { id: SpecService.generateId(), level: 1, text: 'PRODUCTS' },
        { id: SpecService.generateId(), level: 1, text: 'EXECUTION' },
      ],
      systems: [] as any[],
      editHistory: pushEditHistory([], userName, undefined, 'created'),
    };
    try {
      await SpecService.savePublished(master as any);
    } catch (err) {
      // The client-side check above is the common case; this catches the
      // race where another save landed between that check and this one —
      // the server's unique index is the real source of truth (T2.3).
      toast(cleanSaveError(err), true);
      await loadData();
      return;
    }
    setShowNewModal(false);
    setNewCsi('');
    setNewTitle('');
    await loadData();
    onOpenEditor(master.id);
  }

  async function handleDuplicate(e: React.MouseEvent | null, m: any) {
    e?.stopPropagation();
    // No prompt: the copy takes the source number + the next free `.01`,
    // `.02`… (SPEC_WORKFLOW §11), same title.
    const newNumber = nextDuplicateNumber(masters, m.csiNumber || '');
    try {
      const dup = await SpecService.saveAsNewMaster(
        { ...m, csiNumber: newNumber },
        m.title,
        userName
      );
      await loadData();
      if (selectedDiv && dup.division && dup.division !== selectedDiv) setSelectedDiv(dup.division);
      setLibSelected(new Set([dup.id]));
      toast(`Duplicated as ${newNumber}`);
    } catch (err) {
      toast(`Couldn't duplicate: ${cleanSaveError(err)}`, true);
    }
  }

  async function handleOverrideDraft(e: React.MouseEvent | null, m: any) {
    e?.stopPropagation();
    if (!isHeld(m)) return;
    const holder = draftHolder(m) || 'another user';
    setPendingConfirm({
      title: 'Override draft',
      description: `Override ${holder}'s draft for "${m.csiNumber} - ${m.title}"? Their unsaved changes will be discarded.`,
      confirmLabel: 'Override draft',
      destructive: true,
      run: async () => {
        await SpecService.forceOverrideDraft(m.id);
        await loadData();
        toast(`Draft discarded — ${m.csiNumber} has no open draft`);
      },
    });
  }

  async function handleMultiDelete(selected: any[]) {
    const locked = selected.filter((m) => isHeld(m));
    if (locked.length > 0) {
      toast(
        `Cannot delete ${locked.length} spec${locked.length !== 1 ? 's' : ''} with a draft in progress — discard the draft first`,
        true
      );
      return;
    }
    for (const m of selected) {
      await SpecService.deleteMaster(m.id);
    }
    await loadData();
    toast(`Deleted ${selected.length} spec${selected.length !== 1 ? 's' : ''}`);
  }

  async function doArchiveSpecs(targets: any[], archived: boolean) {
    if (!targets.length) return;
    for (const m of targets) await SpecMasterService.setArchived(m.id, archived, userName);
    await loadData();
    toast(
      `${archived ? 'Archived' : 'Restored'} ${targets.length} spec${targets.length === 1 ? '' : 's'}`
    );
  }

  async function handleBulkPublish(selected: any[]) {
    if (!canPublish) {
      toast('You do not have permission to publish specs', true);
      return;
    }
    const publishable = selected.filter((m) => m.status !== 'published' && !hasDraft(m));
    if (publishable.length === 0) {
      toast('No publishable specs selected (already published or draft in progress)', true);
      return;
    }
    const n = publishable.length;
    setPendingConfirm({
      title: 'Publish specs',
      description: `Publish ${n} spec${n !== 1 ? 's' : ''}?`,
      confirmLabel: 'Publish',
      destructive: false,
      run: async () => {
        for (const m of publishable) {
          const updated = {
            ...m,
            status: 'published',
            version: (m.version || 0) + 1,
            lastModified: new Date().toISOString(),
            modifiedBy: userName,
          };
          await SpecService.savePublished(updated);
        }
        await loadData();
        toast(`Published ${n} spec${n !== 1 ? 's' : ''}`);
      },
    });
  }

  async function handleBulkDiscardDraft(selected: any[]) {
    if (!canOverrideDraft) {
      toast('You do not have permission to override drafts', true);
      return;
    }
    const locked = selected.filter((m) => heldByOther(m));
    if (locked.length === 0) {
      toast('No drafts to discard (only drafts held by others can be discarded)', true);
      return;
    }
    const n = locked.length;
    setPendingConfirm({
      title: 'Discard drafts',
      description: `Discard drafts for ${n} spec${n !== 1 ? 's' : ''}? The draft holders' unsaved changes will be lost.`,
      confirmLabel: 'Discard drafts',
      destructive: true,
      run: async () => {
        for (const m of locked) {
          await SpecService.forceOverrideDraft(m.id);
        }
        await loadData();
        toast(`Discarded ${n} draft${n !== 1 ? 's' : ''}`);
      },
    });
  }

  // Per-row "Quick publish" removed (Table Standard R2) — the bulk bar's
  // "Publish N" runs the exact same handleBulkPublish path for one row.

  async function handleOpenReview(draft: SpecMasterDraft) {
    const master = await SpecService.getMaster(draft.blueprintId);
    if (!master) {
      toast('Published master spec not found', true);
      return;
    }
    const diffs = diffBlocks(master.coreContent || [], draft.coreContent || []);
    const summary = diffChangeSummary(master, draft);
    setReviewDraft(draft);
    setReviewDiff(diffs);
    setReviewChangeSummary(summary);
    setReviewNote('');
    setShowReviewModal(true);
  }

  /** Approve = publish, and there is ONE publish path (SPEC_WORKFLOW §7):
   *  open this master in the Studio with the publish dialog up, where the
   *  approver answers every affected variant before anything lands. */
  function handleApprove() {
    if (!reviewDraft) return;
    const masterId = reviewDraft.blueprintId;
    setShowReviewModal(false);
    setReviewDraft(null);
    onOpenEditor(masterId, { openPublish: true });
  }

  async function handleReject() {
    if (!reviewDraft) return;
    await SpecService.rejectPublish(reviewDraft.id, userName, reviewNote || undefined);
    toast('Publish request rejected');
    setShowReviewModal(false);
    setReviewDraft(null);
    await loadData();
  }

  // ─── Import Functions ───

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    const dropped = [...e.dataTransfer.files].filter((f) => isWordFile(f.name));
    if (dropped.length === 0) return;
    // Use the file paths from the drop
    importFromPaths(dropped.map((f) => (f as any).path));
  }

  /** Park the import loop on a conflict dialog until the PM answers. */
  function askConflict(c: ImportConflict): Promise<{ choice: ConflictChoice; all: boolean }> {
    return new Promise((resolve) => {
      conflictResolverRef.current = (choice, all) => {
        conflictResolverRef.current = null;
        resolve({ choice, all });
      };
      setConflictNewCsi(c.csiNumber);
      setConflictNewTitle(c.title);
      setConflictApplyAll(false);
      setImportConflict(c);
    });
  }

  function answerConflict(choice: ConflictChoice) {
    setImportConflict(null);
    conflictResolverRef.current?.(choice, conflictApplyAll);
  }

  async function importFromPaths(paths: string[]) {
    setImporting(true);
    let imported = 0;
    let skipped = 0;
    let lastId = '';
    const failed: ImportFailure[] = [];
    let applyAll: ConflictChoice | null = null;
    knownMastersRef.current = [...masters];
    const sameCsi = (a: string, b: string) => a.replace(/\s/g, '') === b.replace(/\s/g, '');

    for (let i = 0; i < paths.length; i++) {
      const p = paths[i];
      const fname = p.split(/[/\\]/).pop() || p;
      setImportStatus(`Extracting ${i + 1}/${paths.length}: ${fname}`);

      let convertedDocx: string | null = null;
      try {
        let source = p;
        if (isLegacyWordFile(fname)) {
          // .doc / .dot: convert to .docx first (desktop only).
          if (!window.api?.convertWordToDocx) {
            failed.push({ file: fname, reason: LEGACY_WORD_NOT_PROCESSED_MESSAGE });
            continue;
          }
          const conv = await window.api.convertWordToDocx(p);
          if (!conv.ok) {
            failed.push({ file: fname, reason: conv.message });
            continue;
          }
          source = conv.docxPath;
          convertedDocx = conv.docxPath;
        }
        const result = await window.api.extractDocx(source);
        if (!result.success) {
          failed.push({ file: fname, reason: result.error || 'Could not read the file' });
          continue;
        }

        let csi: string = result.csiNumber;
        let title: string = result.title;
        const existing = knownMastersRef.current.find((m) => sameCsi(m.csiNumber || '', csi));
        if (existing) {
          let choice: ConflictChoice;
          if (applyAll) {
            choice =
              applyAll.action === 'skip'
                ? applyAll
                : {
                    action: 'import',
                    csi: nextDuplicateNumber(knownMastersRef.current, csi),
                    title,
                  };
          } else {
            setImportStatus(`Waiting on ${fname}`);
            const answer = await askConflict({
              csiNumber: csi,
              title,
              blocks: result.blocks,
              division: result.division,
              existingMaster: existing,
            });
            choice = answer.choice;
            if (answer.all) applyAll = answer.choice;
          }
          if (choice.action === 'skip') {
            skipped++;
            continue;
          }
          csi = choice.csi;
          title = choice.title;
        }

        const newId = await saveImportedSpec(csi, title, result.division, result.blocks);
        knownMastersRef.current.push({ id: newId, csiNumber: csi, title });
        if (existing) lastId = newId; // only a conflict-renamed import opens the editor, as before
        imported++;
      } catch (err: unknown) {
        failed.push({ file: fname, reason: err instanceof Error ? err.message : String(err) });
      } finally {
        // The converted temp .docx is consumed — remove it.
        if (convertedDocx) await window.api.cleanupConvertedDocx?.(convertedDocx);
      }
    }

    setImporting(false);
    setImportStatus('');
    if (imported > 0) await loadData();
    toast(
      `Imported ${imported} \u00b7 Skipped ${skipped} \u00b7 Failed ${failed.length}`,
      failed.length > 0
    );
    if (failed.length > 0) setImportFailures(failed);
    // A single file that became its own section opens straight into the editor.
    if (paths.length === 1 && imported === 1 && lastId) onOpenEditor(lastId);
  }

  async function handleImport() {
    if (!window.api?.openDocxDialog) {
      toast('File dialog not available', true);
      return;
    }
    const paths = await window.api.openDocxDialog();
    if (!paths?.length) return;
    await importFromPaths(paths);
  }

  async function saveImportedSpec(
    csiNumber: string,
    title: string,
    division: string,
    rawBlocks: any[]
  ) {
    const id = SpecService.generateId();
    const coreContent = rawBlocks.map((b: any, i: number) => ({
      id: SpecService.generateId() + '-' + i,
      level: b.level,
      text: b.text,
      bold: b.bold,
      italic: b.italic,
      underline: b.underline,
      blockType: b.blockType,
      tableHtml: b.tableHtml,
      tableData: b.tableData,
      imageData: b.imageData,
      type: 'fixed' as const,
    }));

    const master = {
      id,
      csiNumber,
      title,
      division: division || csiNumber.substring(0, 2),
      status: 'draft' as const,
      version: 1,
      lastModified: new Date().toISOString(),
      modifiedBy: userName,
      coreContent,
      systems: [] as any[],
      variants: [] as any[],
      editHistory: pushEditHistory([], userName, undefined, 'created'),
    };
    await SpecService.savePublished(master as any);
    return id;
  }

  function handleConflictRename() {
    if (!importConflict) return;
    if (!conflictNewCsi.trim()) {
      toast('Enter a CSI number', true);
      return;
    }
    // Check that the new CSI doesn't also conflict (incl. specs this run just added)
    const stillConflicts = knownMastersRef.current.find(
      (m) => (m.csiNumber || '').replace(/\s/g, '') === conflictNewCsi.replace(/\s/g, '')
    );
    if (stillConflicts) {
      toast('That CSI number also exists — choose a different one', true);
      return;
    }
    answerConflict({
      action: 'import',
      csi: conflictNewCsi.trim(),
      title: conflictNewTitle.trim() || importConflict.title,
    });
  }

  function statusColor(s: string) {
    if (s === 'published' || s === 'active')
      return { bg: 'rgba(var(--okr),0.12)', color: 'var(--ok)', text: 'Published' };
    if (s === 'pending_review') return { bg: WARN_SOFT, color: WARN_TEXT, text: 'Pending review' };
    if (s === 'draft') return { bg: WARN_SOFT, color: WARN_TEXT, text: 'Draft' };
    if (s === 'archived') return { bg: 'var(--sa)', color: 'var(--tm)', text: 'Archived' };
    return { bg: 'var(--sa)', color: 'var(--tm)', text: s || 'Draft' };
  }

  function formatDate(d: string) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  const divTree = divisions.filter((d: any) => divCounts[d.code]);

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left panel — Division Tree (shared with Add Specs) */}
      <DivisionRail
        divisions={divTree.map((d: any) => ({
          code: d.code,
          name: d.name,
          count: divCounts[d.code] || 0,
        }))}
        total={masters.length}
        selected={selectedDiv}
        onSelect={(next, clicked) => {
          setSelectedDiv(next);
          if (clicked) setExpandedDivs((p: any) => ({ ...p, [clicked]: !p[clicked] }));
        }}
        expanded={expandedDivs}
        renderExpanded={(code) => {
          const specsInDiv = masters.filter((m: any) => m.division === code);
          return (
            <>
              {specsInDiv.map((sp: any) => (
                <div
                  key={sp.id}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onOpenEditor(sp.id);
                  }}
                  style={{
                    padding:
                      'var(--space-1) var(--space-3) var(--space-1) calc(var(--space-5) + var(--space-3))',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    color: 'var(--tm)',
                    transition: 'all var(--duration-fast) var(--ease-out)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--ac)')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = 'var(--tm)')}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    {sp.csiNumber}
                  </span>{' '}
                  {sp.title}
                  {sp.lockedBy && (
                    <span
                      title={`Draft in progress — ${sp.lockedBy}`}
                      style={{
                        display: 'inline-flex',
                        verticalAlign: 'middle',
                        marginLeft: 'var(--space-1)',
                        color: WARN_TEXT,
                      }}
                    >
                      <LockIcon em={1} />
                    </span>
                  )}
                </div>
              ))}
            </>
          );
        }}
        footer={
          <>
            {canReview && pendingPRs.length > 0 && (
              <div
                style={{
                  borderTop: '1px solid var(--bd)',
                  padding: 'var(--space-2) var(--space-3)',
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: WARN_TEXT,
                    textTransform: 'uppercase',
                    letterSpacing: '.04em',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Pending reviews ({pendingPRs.length})
                </div>
                {pendingPRs.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => handleOpenReview(d)}
                    style={{
                      padding: 'var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      marginBottom: 'var(--space-1)',
                      border: `1px solid ${WARN_BORDER}`,
                      background: WARN_SOFT,
                      transition: 'all var(--duration-fast) var(--ease-out)',
                    }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = WARN_SOFT_HOVER)
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLElement).style.background = WARN_SOFT)
                    }
                  >
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--tx)',
                      }}
                    >
                      {d.csiNumber} — {d.title}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        marginTop: rem(2),
                      }}
                    >
                      by {d.editedBy} — {d.publishRequest?.message || 'No message'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        }
      />

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-5)',
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sf)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search + bulk ops handled by the DataTable's own bar now
              (docs/FORECASTING_V2_TABLE_STANDARD_PLAN.md) */}
          {/* .docx import needs the local Word engine — desktop only. */}
          {canImport && !isWeb && (
            <Button variant="outline" size="sm" onClick={handleImport} disabled={importing}>
              <DownloadIcon em={1.4} />
              {importing ? importStatus : 'Import .docx'}
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setShowNewModal(true)}>
              <PlusIcon em={1.4} />
              New master spec
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button
            variant={showArchived ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={showArchived}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        </div>

        {/* Cards */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
          style={{ flex: 1, overflow: 'auto', padding: 'var(--space-5)' }}
        >
          {loadError && (
            <div
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                marginBottom: 'var(--space-4)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--dg)',
                background: DANGER_SOFT,
                fontSize: 'var(--font-size-base)',
              }}
            >
              <span aria-hidden="true" style={{ display: 'inline-flex', color: 'var(--dg)' }}>
                <AlertIcon em={1.0769} />
              </span>
              <span style={{ color: 'var(--tx)' }}>{loadError}</span>
            </div>
          )}
          {loading ? (
            <LibrarySkeleton />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<BookIcon em={2.1538} />}
              title={masters.length === 0 ? 'Master spec library' : 'No sections in this division'}
              subtitle={
                masters.length === 0
                  ? "Create master specs to build your firm's specification library."
                  : 'Choose another division, or create a new master spec.'
              }
              action={{
                label: 'Create first master spec',
                onClick: () => setShowNewModal(true),
              }}
            />
          ) : (
            (() => {
              // Full SharePoint-list parity for Spec Masters: DataTable with search,
              // sort by any column, group by any column, column resize, sticky header.
              const columns: Column<any>[] = [
                {
                  key: 'csiNumber',
                  label: 'CSI #',
                  width: 110,
                  getValue: (m) => m.csiNumber || '',
                  render: (m) => (
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        color: 'var(--ac)',
                      }}
                    >
                      {m.csiNumber || '–'}
                    </span>
                  ),
                },
                {
                  key: 'title',
                  label: 'Title',
                  width: 320,
                  getValue: (m) => m.title || '',
                  render: (m) => (
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span
                        style={{
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {m.title}
                        </span>
                        {isHeld(m) && (
                          <span
                            title={
                              hasDraft(m)
                                ? `Draft in progress — ${draftHolder(m)}`
                                : `Draft in progress — ${m.lockedBy}`
                            }
                            style={{ display: 'inline-flex', color: WARN_TEXT }}
                          >
                            <LockIcon em={0.9231} />
                          </span>
                        )}
                      </span>
                      {Array.isArray(m.layers) && m.layers.length > 0 && (
                        <span
                          style={{
                            paddingLeft: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 400,
                            color: 'var(--tm)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={m.layers
                            .map((l: { name?: string }) => l.name || 'Untitled')
                            .join(' · ')}
                        >
                          Variants:{' '}
                          {m.layers
                            .map((l: { name?: string }) => l.name || 'Untitled')
                            .join(' \u00b7 ')}
                        </span>
                      )}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  width: 100,
                  getValue: (m) =>
                    (m.status === 'published' || m.status === 'active') && hasDraft(m)
                      ? 'pending_publish'
                      : m.status || 'draft',
                  render: (m) => {
                    if (m.archivedAt) return <StatusBadge status="archived" />;
                    if ((m.status === 'published' || m.status === 'active') && hasDraft(m))
                      return (
                        <span
                          title={
                            draftHolder(m) ? `Draft by ${draftHolder(m)}` : 'Draft in progress'
                          }
                          style={{ display: 'inline-flex' }}
                        >
                          <StatusBadge
                            status="pending_publish"
                            label="Pending publish"
                            variant="info"
                          />
                        </span>
                      );
                    const sc = statusColor(m.status);
                    return (
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          padding: '2px var(--space-2)',
                          borderRadius: 'var(--radius-sm)',
                          background: sc.bg,
                          color: sc.color,
                          fontWeight: 600,
                        }}
                      >
                        {sc.text}
                      </span>
                    );
                  },
                },
                {
                  key: 'version',
                  label: 'Version',
                  width: 80,
                  getValue: (m) => m.version || 0,
                  render: (m) => <span style={{ color: 'var(--tm)' }}>v{m.version || 1}</span>,
                },
                {
                  key: 'lastModified',
                  label: 'Modified',
                  width: 120,
                  getValue: (m) => m.lastModified || '',
                  render: (m) => (
                    <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                      {formatDate(m.lastModified)}
                    </span>
                  ),
                },
                {
                  key: 'modifiedBy',
                  label: 'Modified by',
                  width: 140,
                  getValue: (m) => m.modifiedBy || '',
                  render: (m) => (
                    <span style={{ color: 'var(--tm)', fontSize: 'var(--font-size-sm)' }}>
                      {m.modifiedBy || '–'}
                    </span>
                  ),
                },
              ];
              const groupOptions: GroupOption<any>[] = columns.map((c) => {
                // Group by CSI # = group by Division (first two digits) — full
                // CSI is too granular. Keyed to the COLUMN so it replaces that
                // column's own grouping instead of sitting beside it.
                if (c.key === 'csiNumber') {
                  return {
                    key: 'csiNumber',
                    label: 'By Division #',
                    getValue: (m: any) => csiDivisionGroupLabel(m.csiNumber),
                  };
                }
                return {
                  key: c.key,
                  label: `By ${c.label}`,
                  getValue: (m: any) => {
                    const v = c.getValue ? c.getValue(m) : (m as any)[c.key];
                    return v == null || v === '' ? `No ${c.label}` : String(v);
                  },
                };
              });
              return (
                <DataTable<any>
                  columns={columns}
                  data={filtered}
                  keyField="id"
                  onRowClick={(m) => onOpenEditor(m.id)}
                  rowHref={(m) => toAddress({ kind: 'spec-studio', masterId: m.id })}
                  selectedIds={libSelected}
                  onSelectionChange={setLibSelected}
                  groupOptions={groupOptions}
                  searchPlaceholder="Search specs (any column)…"
                  initialSort={{ key: 'csiNumber', direction: 'asc' }}
                  storageKey="spec-masters-table"
                  permissions={userPermissions}
                  bulkActions={{
                    archive: {
                      permission: 'specs.archive',
                      onArchive: (items) => doArchiveSpecs(items, true),
                      onUndo: (items) => doArchiveSpecs(items, false),
                      label: 'Archive',
                    },
                    delete: {
                      permission: 'specs.delete',
                      onDelete: handleMultiDelete,
                      confirmNoun: 'master',
                    },
                    exportCsv: { permission: 'specs.export' },
                    extra: (items, meta) => {
                      const archivedOnes = items.filter((m: any) => m.archivedAt);
                      const single = meta?.count === 1 ? items[0] : null;
                      const singleLockedByOther = single && heldByOther(single);
                      return (
                        <>
                          {canPublish && (
                            <button
                              onClick={() => handleBulkPublish(items)}
                              style={{
                                padding: 'var(--space-2) var(--space-3)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--ok)',
                                background: 'rgba(var(--okr), 0.08)',
                                color: 'var(--ok)',
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                minHeight: rem(32),
                              }}
                            >
                              <PublishIcon em={1} /> Publish {items.length}
                            </button>
                          )}
                          {canOverrideDraft && (
                            <button
                              onClick={() => handleBulkDiscardDraft(items)}
                              style={{
                                padding: 'var(--space-2) var(--space-3)',
                                borderRadius: 'var(--radius-md)',
                                border: `1px solid ${WARN_BORDER}`,
                                background: WARN_SOFT,
                                color: WARN_TEXT,
                                fontSize: 'var(--font-size-base)',
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                minHeight: rem(32),
                              }}
                            >
                              <UnlockIcon em={1} /> Discard drafts
                            </button>
                          )}
                          {canOverrideDraft && singleLockedByOther && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void handleOverrideDraft(null, single)}
                            >
                              <UnlockIcon em={1} /> Override draft
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              meta?.clear();
                              for (const m of items) await handleDuplicate(null, m);
                            }}
                          >
                            <CopyIcon em={1} /> Duplicate
                          </Button>
                          {permCan('specs.export') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExportTargets(items as SpecMaster[])}
                            >
                              <DownloadIcon em={1} /> Export…
                            </Button>
                          )}
                          {archivedOnes.length > 0 && canArchive && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => void doArchiveSpecs(archivedOnes, false)}
                            >
                              Restore {archivedOnes.length}
                            </Button>
                          )}
                        </>
                      );
                    },
                  }}
                />
              );
            })()
          )}
        </div>
      </div>

      {/* New Spec Master Modal */}
      {showNewModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowNewModal(false);
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--font-size-lg)',
                marginBottom: 'var(--space-4)',
              }}
            >
              New master spec
            </div>
            <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                CSI number
              </div>
              <input
                value={newCsi}
                onChange={(e) => setNewCsi(e.target.value)}
                placeholder="e.g. 09 21 16"
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-mono)',
                }}
                autoFocus
              />
            </label>
            <label style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Title
              </div>
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Gypsum Board Assemblies"
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 'var(--font-size-base)',
                }}
              />
            </label>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 'var(--space-2)',
                marginTop: 'var(--space-5)',
              }}
            >
              <Button variant="ghost" onClick={() => setShowNewModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleNew}>Create</Button>
            </div>
          </div>
        </div>
      )}

      {/* PR Review Modal (admin) */}
      {showReviewModal && reviewDraft && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowReviewModal(false);
          }}
          style={overlayStyle}
        >
          <div style={{ ...modalStyle, width: rem(640), maxHeight: '85vh' }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--font-size-lg)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Review publish request
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              <strong>{reviewDraft.csiNumber}</strong> — {reviewDraft.title}
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Submitted by <strong>{reviewDraft.editedBy}</strong> on{' '}
              {formatDate(reviewDraft.publishRequest?.requestedAt || '')}
            </div>
            {reviewDraft.publishRequest?.message && (
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--tx)',
                  marginBottom: 'var(--space-3)',
                  fontStyle: 'italic',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--sa)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                "{reviewDraft.publishRequest.message}"
              </div>
            )}

            {/* Diff summary */}
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              <strong>Content:</strong> {reviewDiff.filter((d) => d.type === 'added').length} added,{' '}
              {reviewDiff.filter((d) => d.type === 'removed').length} removed,{' '}
              {reviewDiff.filter((d) => d.type === 'modified').length} modified
            </div>
            {reviewChangeSummary &&
              (() => {
                const cs = reviewChangeSummary;
                const parts: string[] = [];
                if (cs.systemsAdded.length) parts.push(`${cs.systemsAdded.length} system(s) added`);
                if (cs.systemsRemoved.length)
                  parts.push(`${cs.systemsRemoved.length} system(s) removed`);
                if (cs.systemsModified.length)
                  parts.push(`${cs.systemsModified.length} system(s) modified`);
                if (cs.variantsAdded.length)
                  parts.push(`${cs.variantsAdded.length} option(s) added`);
                if (cs.variantsRemoved.length)
                  parts.push(`${cs.variantsRemoved.length} option(s) removed`);
                if (cs.variantsModified.length)
                  parts.push(`${cs.variantsModified.length} option(s) modified`);
                if (parts.length === 0) return null;
                return (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    {parts.join(', ')}
                  </div>
                );
              })()}

            {/* Diff content */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-1)',
                maxHeight: '40vh',
                overflow: 'auto',
                marginBottom: 'var(--space-4)',
              }}
            >
              {reviewDiff
                .filter((d) => d.type !== 'unchanged')
                .map((d, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 'var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: 'var(--font-size-sm)',
                      border: '1px solid var(--bd)',
                      background:
                        d.type === 'added'
                          ? 'rgba(var(--okr),0.06)'
                          : d.type === 'removed'
                            ? 'color-mix(in srgb, var(--dg) 6%, transparent)'
                            : 'color-mix(in srgb, var(--color-warning) 6%, transparent)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        padding: '1px var(--space-1)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--color-text-inverse)',
                        marginRight: 'var(--space-2)',
                        background:
                          d.type === 'added'
                            ? 'var(--ok)'
                            : d.type === 'removed'
                              ? 'var(--dg)'
                              : 'var(--color-warning)',
                      }}
                    >
                      {d.type === 'added' ? '+' : d.type === 'removed' ? '-' : '~'}
                    </span>
                    {d.type === 'modified' && (
                      <span>
                        <span style={{ color: 'var(--dg)', textDecoration: 'line-through' }}>
                          {d.oldText}
                        </span>{' '}
                        → <span style={{ color: 'var(--ok)' }}>{d.newText}</span>
                      </span>
                    )}
                    {d.type === 'added' && <span style={{ color: 'var(--ok)' }}>{d.newText}</span>}
                    {d.type === 'removed' && (
                      <span style={{ color: 'var(--dg)', textDecoration: 'line-through' }}>
                        {d.oldText}
                      </span>
                    )}
                  </div>
                ))}
              {reviewDiff.filter((d) => d.type !== 'unchanged').length === 0 && (
                <div
                  style={{
                    padding: 'var(--space-3)',
                    textAlign: 'center',
                    color: 'var(--tm)',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  No content changes detected.
                </div>
              )}
            </div>

            <label style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  color: 'var(--tm)',
                  marginBottom: 'var(--space-1)',
                }}
              >
                Review note (optional)
              </div>
              <input
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Optional feedback..."
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--ib)',
                  color: 'var(--tx)',
                  fontSize: 'var(--font-size-base)',
                }}
              />
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
              <Button variant="ghost" onClick={() => setShowReviewModal(false)}>
                Cancel
              </Button>
              <button
                onClick={handleReject}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--dg)',
                  background: 'transparent',
                  color: 'var(--dg)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: rem(32),
                }}
              >
                Reject
              </button>
              <button
                onClick={handleApprove}
                style={{
                  padding: 'var(--space-2) var(--space-5)',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'var(--ok)',
                  color: 'var(--color-text-inverse)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: rem(32),
                }}
              >
                Approve & publish
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Import Conflict Modal */}
      {importConflict && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) answerConflict({ action: 'skip' });
          }}
          style={overlayStyle}
        >
          <div style={modalStyle}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 'var(--font-size-lg)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Section already exists
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                marginBottom: 'var(--space-4)',
                lineHeight: 1.5,
              }}
            >
              <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--ac)' }}>
                {importConflict.csiNumber}
              </strong>{' '}
              — {importConflict.title}
              <br />
              already matches <strong>{importConflict.existingMaster.csiNumber}</strong> —{' '}
              {importConflict.existingMaster.title}
            </div>

            {/* Change number/name, then import as its own section */}
            <div
              style={{
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--bd)',
                marginBottom: 'var(--space-3)',
                background: 'var(--sa)',
              }}
            >
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 600,
                  marginBottom: 'var(--space-2)',
                }}
              >
                Give it a different number or title
              </div>
              <div
                style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    CSI number
                  </div>
                  <input
                    value={conflictNewCsi}
                    onChange={(e) => setConflictNewCsi(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--bd)',
                      background: 'var(--ib)',
                      color: 'var(--tx)',
                      fontSize: 'var(--font-size-base)',
                      fontFamily: 'var(--font-mono)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 2 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    Title
                  </div>
                  <input
                    value={conflictNewTitle}
                    onChange={(e) => setConflictNewTitle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2) var(--space-3)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--bd)',
                      background: 'var(--ib)',
                      color: 'var(--tx)',
                      fontSize: 'var(--font-size-base)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
              <Button onClick={handleConflictRename} style={{ width: '100%' }}>
                Import as new section
              </Button>
            </div>

            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontSize: 'var(--font-size-base)',
                marginBottom: 'var(--space-3)',
                cursor: 'pointer',
              }}
            >
              <Checkbox
                checked={conflictApplyAll}
                onCheckedChange={setConflictApplyAll}
                aria-label="Apply to all remaining conflicts"
              />
              Apply to all remaining conflicts
            </label>
            <Button
              variant="outline"
              onClick={() => answerConflict({ action: 'skip' })}
              style={{ width: '100%' }}
            >
              Skip this file
            </Button>
          </div>
        </div>
      )}

      {/* Confirmations (replaces native confirm()) */}
      <ConfirmDialog
        open={!!pendingConfirm}
        onOpenChange={(o) => !o && setPendingConfirm(null)}
        title={pendingConfirm?.title ?? ''}
        description={pendingConfirm?.description ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel}
        destructive={pendingConfirm?.destructive ?? true}
        busy={confirmBusy}
        onConfirm={async () => {
          const job = pendingConfirm;
          if (!job) return;
          setConfirmBusy(true);
          try {
            await job.run();
          } catch (err) {
            toast(`Couldn't finish: ${cleanSaveError(err)}`, true);
          } finally {
            setConfirmBusy(false);
            setPendingConfirm(null);
          }
        }}
      />

      {/* Import: files that could not be brought in */}
      <Dialog open={!!importFailures} onOpenChange={(o) => !o && setImportFailures(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Some files weren&rsquo;t imported</DialogTitle>
            <DialogDescription>
              {importFailures?.length ?? 0} file{importFailures?.length === 1 ? '' : 's'}{' '}
              couldn&rsquo;t be imported.
            </DialogDescription>
          </DialogHeader>
          <ul
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
              maxHeight: rem(260),
              overflow: 'auto',
              margin: 0,
              padding: 0,
              listStyle: 'none',
              fontSize: 'var(--font-size-base)',
            }}
          >
            {(importFailures ?? []).map((f, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  padding: 'var(--space-2) var(--space-3)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--sa)',
                }}
              >
                <span style={{ fontWeight: 600 }}>{f.file}</span>
                <span style={{ color: 'var(--dg)' }}>{f.reason}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setImportFailures(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {exportTargets && (
        <ExportMastersDialog
          open
          onOpenChange={(o) => !o && setExportTargets(null)}
          masters={exportTargets}
          userPermissions={userPermissions}
          userName={userName}
          toast={toast}
        />
      )}
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,0,0,.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: 'var(--sf)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  width: rem(420),
  border: '1px solid var(--bd)',
  boxShadow: 'var(--shadow-xl)',
  maxHeight: '80vh',
  overflow: 'auto',
};

(window as any).SpecLibrary = SpecLibrary;
export { SpecLibrary };
